// Local conversation history.
//
// Server-side state lives in the in-process cache (30 min TTL) and is needed
// for chart-modification context. Client-side, we keep a richer index here so
// users can see and switch between past chats even after the server cache has
// dropped them.

const INDEX_KEY = "vizaura_conversations";
const ACTIVE_KEY = "vizaura_conversation_id";

function read() {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(list));
  } catch {
    // Quota exceeded or storage disabled — fail quietly.
  }
}

export function genId() {
  return (
    (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
    `conv-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`
  );
}

export function listConversations() {
  // Pinned first, then most recently updated.
  return read().sort((a, b) => {
    if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1;
    return (b.updatedAt || 0) - (a.updatedAt || 0);
  });
}

export function togglePin(id) {
  const list = read();
  const idx = list.findIndex((c) => c.id === id);
  if (idx < 0) return false;
  list[idx] = { ...list[idx], pinned: !list[idx].pinned };
  write(list);
  return list[idx].pinned;
}

export function getConversation(id) {
  return read().find((c) => c.id === id) || null;
}

export function getActiveId() {
  return localStorage.getItem(ACTIVE_KEY);
}

export function setActiveId(id) {
  if (id) localStorage.setItem(ACTIVE_KEY, id);
  else localStorage.removeItem(ACTIVE_KEY);
}

export function upsertConversation(partial) {
  if (!partial || !partial.id) return;
  const list = read();
  const idx = list.findIndex((c) => c.id === partial.id);
  const merged = {
    id: partial.id,
    title: partial.title || "New chat",
    messages: partial.messages || [],
    chartUrl: partial.chartUrl || "",
    chartSubtitle: partial.chartSubtitle || "",
    updatedAt: Date.now(),
  };
  // Per-chat selections (vizapp passes these; switchConversation reads them back
  // so reopening a chat restores its database/project/dataset/chart context).
  // Only overwrite when the caller provided a value, so a lightweight upsert
  // (e.g. title-only) never wipes a previously-stored selection.
  const PER_CHAT = ["dbId", "datasetMode", "datasetId", "projectId", "chartMode", "activeChartId", "pinned"];
  for (const k of PER_CHAT) {
    if (partial[k] !== undefined) merged[k] = partial[k];
  }
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...merged };
  } else {
    list.push(merged);
  }
  write(list);
  return merged;
}

export function deleteConversation(id) {
  write(read().filter((c) => c.id !== id));
}

export function createConversation() {
  const id = genId();
  upsertConversation({ id, title: "New chat", messages: [] });
  setActiveId(id);
  return id;
}

export function deriveTitle(messages) {
  // Use the first user message as the conversation title (truncated).
  const firstUser = messages.find((m) => m.sender === "user");
  if (!firstUser) return "New chat";
  const text = String(firstUser.text || "").trim();
  return text.length > 48 ? text.slice(0, 45) + "..." : text || "New chat";
}
